/**
 * ProShip Direct v2 — connect a courier account.
 *
 * The one step in this module with no ProShip default behind it: credentials cannot be inherited,
 * so an account that has not passed its test cannot ship.
 *
 * Laid out as four numbered steps with a standing summary beside them, because a merchant doing
 * this is reading a courier agreement and a carrier's API email at the same time and needs to see
 * through they are without scrolling. The credential fields are the carrier's own — Bluedart asks
 * for a Licence key, Xpressbees for an email and password — so the labels match what the merchant
 * is looking at rather than a normalised "API key".
 *
 * The carrier list is a live read of the ProShip catalogue, so a merchant can only connect an
 * account for a carrier ProShip can actually talk to.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, Grid, Typography, Button, CircularProgress, Alert, Tooltip, Divider,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import withLayout from '../../../hocs/Layout';
import PageTitle from '../../../common/PageTitle';
import Loader from '../../../common/Loader';
import { FormProvider, RHFTextField, RHFSelectField } from '../../../common/hook-form';
import {
  pageContainerSx, sectionCardSx, primaryBtnSx, secondaryBtnSx,
} from '../../../styles/patterns';
import { SUBTLE, NAVY, SUCCESS, WARNING, BORDER, LIGHT_BG, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP } from '../../../styles/foundation/spacing';

import { usePlan, useCourierCatalogue, useDirectCourierSetup, countsAgainstPlan } from '../data/hooks';
import { COURIER_META_FIELDS, defaultCourierMeta } from '../data/defaults';
import { MONO } from '../components/directCellStyles';
import { dpPath } from '../flags';
import { PlanInactive, CatalogueError } from '../components/states';
import {
  CourierMetaFields,
  WebhookPanel,
} from '../components/courierFields';

const bodySx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.6 };
const strongSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };
const stepTitleSx = { fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY };

/*
  WHAT THE TEST ACTUALLY DOES, and why it is one check rather than three.

  An earlier version of this screen showed three ticks — authenticate, register a test AWB, pull
  tracking — and filled them in on a timer. Two of them described things the real probe
  deliberately does NOT do: it never creates a shipment, because a test that leaves a live
  consignment behind is a test nobody runs twice.

  What the server really does, per carrier: for one with a login step it drops any cached token
  and authenticates afresh; for one that carries a static key on every request it tracks a
  synthetic AWB, where "no such consignment" is the pass — getting that answer means the host
  resolved and the key was accepted. Either way it proves the credentials, the base URL and the
  cp_id in one call, and proves nothing about contract entitlements, which only a real shipment
  can show.
*/
/*
  Shown BEFORE a test runs, to set expectations. Afterwards the server's own `verified` list
  replaces it, because what a pass proves differs per carrier and only the adapter knows which
  probe it ran — a login proves more than a tracking call, and for DTDC the tracking credential
  is not even the one that creates shipments. These three sentences used to be rendered beside
  every green tick regardless, which made a claim the probe had not earned.
*/
/** service_type_master categories, in merchant words rather than enum words. */
const CATEGORY_LABELS = {
  FORWARD: 'Standard delivery',
  REVERSE: 'Returns pickup',
  SDD_NDD: 'Same-day / next-day',
  HIGH_VALUE: 'High value',
  HYPERLOCAL: 'Hyperlocal',
};

const TEST_SCOPE = [
  'That the carrier accepts the credentials you entered',
  'That we are calling the right carrier endpoint for this account',
  'That the account is wired to the right carrier contract (cp_id)',
];

const ACCOUNT_CODE_RE = /^[A-Za-z0-9_-]+$/;

const schema = Yup.object().shape({
  carrierName: Yup.string().required('Pick the courier this account is with'),
  accountCode: Yup.string()
    .required('Give the account a code you will recognise')
    .matches(ACCOUNT_CODE_RE, 'Letters, numbers, hyphen and underscore only')
    .max(64, 'Up to 64 characters'),
  /*
    Direction and mode replace the old single 'Service' field. They are different questions and
    the backend uses them differently: direction picks the PCIS cp_id, mode does not (PCIS has no
    dispatch-mode column at all) and is recorded on the ProShip routing document instead.
  */
  direction: Yup.string().required('Forward or reverse?'),
  mode: Yup.string().required('Surface or air?'),
  weightBand: Yup.string().required('Regular or heavy?'),
  serviceTypePrimary: Yup.string().required('Pick the service this contract covers'),
});

/** One line in the standing summary: done, or not yet. */
function StepState({ done, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.4 }}>
      {done ? (
        <CheckCircleIcon sx={{ fontSize: 15, color: SUCCESS, mt: '1px' }} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: 15, color: SUBTLE, mt: '1px' }} />
      )}
      <Typography sx={{ ...bodySx, color: done ? NAVY : SUBTLE }}>{children}</Typography>
    </Box>
  );
}

/** A numbered step card, so the sequence reads as a sequence. */
function Step({ n, title, hint, children }) {
  return (
    <Box sx={sectionCardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, mb: hint ? 0.4 : 1.5 }}>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            bgcolor: `${PRIMARY_BLUE}14`,
            color: PRIMARY_BLUE,
            fontFamily: FONT,
            fontSize: SIZE['3xs'],
            fontWeight: WEIGHT.bold,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {n}
        </Box>
        <Typography sx={stepTitleSx}>{title}</Typography>
      </Box>
      {hint && <Typography sx={{ ...bodySx, mb: 1.5, ml: 3.75 }}>{hint}</Typography>}
      {children}
    </Box>
  );
}

function ConnectCourier() {
  const navigate = useNavigate();
  const { plan, usage } = usePlan();
  const { couriers: catalogue, isLoading, isError, refetch } = useCourierCatalogue();
  /*
    The server owns the courier list AND the credential fields. The list is PCIS's NATIVE
    partners filtered to those with a schema, so a merchant can never pick a courier we cannot
    actually onboard; the fields come from proship_direct_courier_schema, so their keys match
    what the PCIS adapter deserialises. A fixture here would create accounts that look fine and
    fail at the first AWB.
  */
  const direct = useDirectCourierSetup();
  const [credFieldsApi, setCredFieldsApi] = useState([]);
  const [cpCandidates, setCpCandidates] = useState([]);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [testing, setTesting] = useState(false);
  /* { success, message, testedAt } from the server — null until the merchant runs the test. */
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);
  /* { code, available, message } for the code last checked — null until a well-formed code is typed. */
  const [codeCheck, setCodeCheck] = useState(null);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      carrierName: '', accountCode: '', direction: '', mode: '', weightBand: '',
      category: '', serviceTypePrimary: '', cred: {}, meta: defaultCourierMeta(),
    },
  });
  const { watch, setValue, handleSubmit, formState: { isValid } } = methods;

  const carrierName = watch('carrierName');
  const accountCode = watch('accountCode');
  const cred = watch('cred') || {};
  const meta = watch('meta') || {};

  // Parent courier is what a merchant recognises — one option per carrier, not per courier code.
  /* PCIS NATIVE partners that have a credential schema — see useDirectCourierSetup. */
  const carrierOptions = useMemo(
    () => direct.parents.map((p) => ({ value: p.courierCode, label: p.courierName || p.courierCode })),
    [direct.parents]
  );

  /*
    THE SERVICE CATALOGUE, and why the form asks four questions rather than two.

    `service_type_primary` is what allocation and reporting group by, and the key the server
    reads the default-account map with. It used to be invented here as FWD_<mode>_REG, which
    labelled every account REGULAR — so a heavy or same-day contract was registered under a
    service it does not run, and inherited the wrong coverage.

    Direction and mode cannot identify it: five codes share FORWARD/SURFACE/REGULAR (standard,
    same-day, same-city NDD, intercity NDD, high value) and five more share FORWARD/SURFACE/
    LARGE. Weight band separates regular from heavy, category separates the rest. So: four
    answers, then the merchant picks the named service.

    The whole catalogue is fetched once and narrowed here, so the dropdowns come from the data —
    a code added to service_type_master appears without a frontend change.
  */
  const [serviceTypes, setServiceTypes] = useState([]);

  /*
    DIRECTION IS THE CARRIER'S ANSWER, NOT A FIXED PAIR OF RADIO BUTTONS.

    Offering Forward and Reverse to everyone invites a merchant to pick a direction we have not
    integrated and then read a rejection — DTDC is forward-only (one cp_id, 8), Shadowfax Reverse
    is reverse-only, and in production twelve of the fourteen native carriers support exactly one
    direction. Only Delhivery and Ekart genuinely offer both.

    The list comes from /couriers/parents, which derives it from the PCIS courier_partners rows
    that actually exist for that carrier. A direction that is not integrated is not an option.

    ANY IS A WIRE VALUE, NOT A LABEL. Carriers with no forward/reverse split in PCIS (ATS,
    PiknDel) carry a null service_type, which the server reports as ANY; the merchant should read
    that as "Forward", which is what the ProShip routing document records. So the label is
    flattened here and the value is passed through untouched — the server matches on it.
  */
  const selectedParent = useMemo(
    () => direct.parents.find((p) => p.courierCode === carrierName),
    [direct.parents, carrierName]
  );
  const directionOptions = useMemo(
    () => (selectedParent?.directions || []).map((d) => ({
      value: d,
      label: d === 'RVP' ? 'Reverse' : 'Forward',
    })),
    [selectedParent]
  );
  const directionLocked = directionOptions.length === 1;

  useEffect(() => {
    if (!carrierName) { setServiceTypes([]); return undefined; }
    let alive = true;
    direct.loadServiceTypes(carrierName)
      .then((list) => { if (alive) setServiceTypes(list); })
      .catch((e) => { if (alive) { setServiceTypes([]); setSaveError(e.message); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierName]);

  /*
    Preselect when there is only one. Leaving a single-option select empty makes the merchant
    click to confirm something they were never given a choice about, and until they do, step 1
    reads as incomplete and the connection test stays disabled.
  */
  const soleDirection = directionLocked ? directionOptions[0].value : null;
  useEffect(() => {
    setValue('direction', soleDirection || '', { shouldValidate: true });
  }, [carrierName, soleDirection, setValue]);

  /*
    Completeness is measured against the SERVER schema (credFieldsApi), not a fixture — it is
    the same list the backend validates against, so "looks complete" here and "accepted" there
    cannot disagree. Note fieldKey, not name: the key is what goes into PCIS credentials_json.
  */
  const missingCreds = credFieldsApi.filter((f) => f.required && !cred[f.fieldKey]);
  const missingMeta = COURIER_META_FIELDS.filter(
    (f) => f.required && (meta[f.key] === undefined || meta[f.key] === '')
  );

  const direction = watch('direction');
  const mode = watch('mode');

  /*
    Step 4 and 6, together: the moment courier + direction are known, ask the server which cp_id
    applies and which fields to render. Both are server answers — resolving cp_id client-side
    would mean duplicating a rule that production data shows is not derivable from the three
    questions anyway (DTDC has five FORWARD rows).
  */
  useEffect(() => {
    if (!carrierName || !direction) { setCredFieldsApi([]); setCpCandidates([]); return; }
    let alive = true;
    direct.loadSchema(carrierName, direction)
      .then((f) => { if (alive) setCredFieldsApi(f); })
      .catch((e) => { if (alive) { setCredFieldsApi([]); setSaveError(e.message); } });
    direct.resolveCp(carrierName, direction, mode)
      .then((c) => { if (alive) setCpCandidates(c); })
      .catch(() => { if (alive) setCpCandidates([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierName, direction, mode]);

  /*
    Narrowing, in the order the merchant answers. Category options are offered only for the
    direction + mode + band already chosen, and the service list only for all four — so a
    combination with no service simply has nothing to pick rather than an error after the fact.

    ANY is a wire value for direction, not a category of service: carriers PCIS reports no split
    for are forward in every service_type_master row, so it maps to FORWARD here.
  */
  const weightBand = watch('weightBand');
  const category = watch('category');
  const stDirection = direction === 'RVP' ? 'RVP' : 'FORWARD';

  const bandOptions = useMemo(() => {
    const bands = serviceTypes
      .filter((t) => t.direction === stDirection && (!mode || t.mode === mode))
      .map((t) => t.weightBand);
    return [...new Set(bands)].map((b) => ({ value: b, label: b === 'LARGE' ? 'Heavy / large' : 'Regular' }));
  }, [serviceTypes, stDirection, mode]);

  const categoryOptions = useMemo(() => {
    const cats = serviceTypes
      .filter((t) => t.direction === stDirection && (!mode || t.mode === mode)
        && (!weightBand || t.weightBand === weightBand))
      .map((t) => t.category);
    return [...new Set(cats)].map((c) => ({ value: c, label: CATEGORY_LABELS[c] || c }));
  }, [serviceTypes, stDirection, mode, weightBand]);

  const serviceOptions = useMemo(
    () => serviceTypes
      .filter((t) => t.direction === stDirection && (!mode || t.mode === mode)
        && (!weightBand || t.weightBand === weightBand)
        && (!category || t.category === category))
      .map((t) => ({ value: t.code, label: t.name })),
    [serviceTypes, stDirection, mode, weightBand, category]
  );

  /*
    ASK ONLY WHAT WE CANNOT WORK OUT.

    Of the thirteen reachable combinations of direction, mode, size and speed, eight identify a
    single service — asking the merchant to then pick it from a list of one is asking them to
    confirm our own arithmetic, and it reads as though we are unsure.

    Five are genuinely ambiguous and stay a question, because only the merchant knows the answer:
    same-day vs same-city next-day vs intercity next-day, reverse with or without a doorstep
    quality check, and the three hyperlocal windows.

    Same rule for speed: a direction and mode with only one category does not need a dropdown.
    Direction already works this way — see directionLocked.
  */
  const soleCategory = categoryOptions.length === 1 ? categoryOptions[0].value : null;
  useEffect(() => {
    if (soleCategory) setValue('category', soleCategory, { shouldValidate: true });
  }, [soleCategory, setValue]);

  const soleService = serviceOptions.length === 1 ? serviceOptions[0].value : null;
  /*
    Keyed on every narrowing input, not just soleService: changing the size from Regular to Heavy
    moves from one ambiguous set to another, and without the wider dependency the merchant's
    earlier pick would survive into a combination it does not belong to.
  */
  useEffect(() => {
    setValue('serviceTypePrimary', soleService || '', { shouldValidate: true });
  }, [carrierName, stDirection, mode, weightBand, category, soleService, setValue]);

  const chosenService = serviceTypes.find((t) => t.code === watch('serviceTypePrimary'));

  /*
    A pass belongs to the exact values that were tested. Edit a credential after a green tick and
    the tick is no longer about what Save would write, so it is dropped — otherwise the merchant
    could pass with one licence key and register another.
  */
  /*
    THE ACCOUNT CODE MUST BE NEW, in ProShip and in PCIS — it is the key every shipment is routed
    by. Checked as the merchant types (debounced) so they hear it in step 1, not after filling in
    credentials and running the test. Save checks again on the server; this is the early warning.
    An unreachable check blocks nothing: the server's own check at test and Save still applies.
  */
  useEffect(() => {
    const code = (accountCode || '').trim();
    if (!code || code.length > 64 || !ACCOUNT_CODE_RE.test(code)) { setCodeCheck(null); return undefined; }
    let alive = true;
    const timer = setTimeout(() => {
      direct.checkAccountCode(code)
        .then((res) => { if (alive && res) setCodeCheck({ code, available: res.available !== false, message: res.message }); })
        .catch(() => { if (alive) setCodeCheck(null); });
    }, 400);
    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountCode]);
  const codeTaken = codeCheck?.code === (accountCode || '').trim() && codeCheck.available === false;

  const credFingerprint = JSON.stringify(cred);
  useEffect(() => {
    setTestResult(null);
    setTestError(null);
  }, [carrierName, accountCode, direction, mode, credFingerprint]);

  /*
    `serviceType` used to be a single field here and was split into direction + mode; the check
    kept watching the old name, which no longer exists, so accountDone was permanently false and
    the connection test could never be enabled. Both halves of the split are in the yup schema,
    so isValid already covers them.
  */
  const accountDone = Boolean(carrierName && accountCode && !codeTaken && direction && mode
    && watch('weightBand') && watch('serviceTypePrimary') && isValid);
  const credsDone = accountDone && missingCreds.length === 0;
  const metaDone = missingMeta.length === 0;
  const testDone = testResult?.success === true;

  /*
    Against the REGISTERED accounts, not the local fixture this banner used to read. The plan
    caps active couriers, so a switched-off or retired one leaves room — the server applies the
    same rule on save, and a banner that disagreed with it would block a registration the server
    would have accepted.
  */
  const allowed = usage.plan?.courierIntegrations || 0;
  const counted = direct.accounts.filter(countsAgainstPlan).length;
  const atLimit = allowed > 0 && counted >= allowed;

  /**
   * Calls the carrier for real, through proship_mongo and PCIS. Nothing is saved either way.
   *
   * Two failure shapes and they are not the same thing. A carrier that rejects the credentials
   * answers {success:false, message} — that is a RESULT, rendered in place with the carrier's
   * own words, and it is the likeliest outcome of this button. A request that could not be made
   * at all (no plan, a field the server rejects, PCIS unreachable) throws, and the server's
   * sentence goes to testError.
   */
  const runTest = async (values) => {
    setTesting(true);
    setTestResult(null);
    setTestError(null);
    try {
      const res = await direct.testCredentials({
        courierCode: values.carrierName,
        direction: values.direction,
        mode: values.mode,
        serviceTypePrimary: values.serviceTypePrimary,
        /* Omitted when the server can resolve it; sent only when the merchant had to choose. */
        cpId: cpCandidates.length === 1 ? cpCandidates[0].cpId : (values.cpId || null),
        accountCode: values.accountCode,
        credentials: values.cred,
      });
      setTestResult(res);
    } catch (e) {
      setTestError(e.message);
    } finally {
      setTesting(false);
    }
  };

  /**
   * Registers for real: creates the account in PCIS and the routing document in ProShip.
   *
   * Can fail for reasons the merchant must read verbatim — no active plan, the plan's courier
   * limit reached, or PCIS rejecting the credentials — so this awaits and surfaces the server's
   * own sentence rather than navigating away optimistically.
   */
  const onSubmit = async (values) => {
    if (!testDone) {
      setTestError('Run the connection test before saving — an untested account cannot ship.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await direct.register({
        courierCode: values.carrierName,
        direction: values.direction,
        mode: values.mode,
        serviceTypePrimary: values.serviceTypePrimary,
        // Omitted when there is exactly one candidate: the server resolves it. Sent only when
        // the merchant had to choose (DTDC is the one courier with several).
        cpId: cpCandidates.length === 1 ? cpCandidates[0].cpId : (values.cpId || null),
        accountCode: values.accountCode,
        displayName: values.accountCode,
        credentials: values.cred,
        /*
          Step 3, which until now was collected and thrown away. The server writes most of these
          onto the same courier_partners fields allocation already reads for a ProShip courier —
          min chargeable weight, max weight per piece, the dimension ceilings, the pickup cutoff
          — so the hint on that step ("Allocation reads these before it hands a shipment over")
          is true rather than aspirational. Weights go up in grams and are converted server-side.
        */
        meta: values.meta,
      });
      navigate(dpPath('/couriers'));
    } catch (e) {
      setSaveError(e?.message || 'Could not register this courier.');
    } finally {
      setSaving(false);
    }
  };

  if (!plan.active) {
    return (
      <Box sx={pageContainerSx}>
        <PageTitle>Connect a courier</PageTitle>
        <PlanInactive what='Connecting your own couriers' />
      </Box>
    );
  }

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='Bring a courier account you already hold onto ProShip'>
        Connect a courier
      </PageTitle>

      {isError && <CatalogueError onRetry={refetch} />}
      {atLimit && (
        <Alert severity='warning' sx={{ mb: 2, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
          The {usage.plan?.name} plan includes {allowed} courier accounts and you have connected{' '}
          {counted}. Switch one off, retire it, or upgrade from the{' '}
          <Link to={dpPath('/home')} style={{ color: 'inherit' }}>Direct home page</Link> to connect
          more.
        </Alert>
      )}

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          {/* ── The steps ── */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.lg }}>
              <Step
                n={1}
                title='Which courier is this?'
                hint='Hold more than one account with the same courier? Connect each separately — prepaid, COD, air and surface are priced and served differently.'
              >
                {isLoading ? (
                  <Loader />
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <RHFSelectField
                        name='carrierName'
                        label='Carrier'
                        width='100%'
                        options={carrierOptions}
                        mandatory
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <RHFTextField
                        name='accountCode'
                        label='Account code'
                        mandatory
                        placeholder='ABC_DL_SURFACE_PREPAID'
                        {...(codeTaken ? { error: true, helperText: codeCheck.message } : {})}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <RHFSelectField
                        name='direction'
                        label='Direction'
                        width='100%'
                        options={directionOptions}
                        disabled={!carrierName || directionLocked}
                        mandatory
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <RHFSelectField
                        name='mode'
                        label='Mode'
                        width='100%'
                        options={[
                          { value: 'SURFACE', label: 'Surface' },
                          { value: 'AIR', label: 'Air' },
                        ]}
                        mandatory
                      />
                    </Grid>

                    {/*
                      Weight band and category: the two answers that turn direction + mode into
                      one service. Without them five codes are indistinguishable, and the old
                      form silently picked REGULAR for all of them.
                    */}
                    <Grid item xs={12} md={3}>
                      <RHFSelectField
                        name='weightBand'
                        label='Shipment size'
                        width='100%'
                        options={bandOptions}
                        disabled={!carrierName || bandOptions.length === 0}
                        mandatory
                      />
                    </Grid>
                    {/* Only shown when there is more than one answer — see the note above. */}
                    {categoryOptions.length > 1 && (
                      <Grid item xs={12} md={3}>
                        <RHFSelectField
                          name='category'
                          label='Service speed'
                          width='100%'
                          options={categoryOptions}
                          disabled={!weightBand}
                          mandatory
                        />
                      </Grid>
                    )}
                    {serviceOptions.length > 1 && (
                      <Grid item xs={12} md={6}>
                        <RHFSelectField
                          name='serviceTypePrimary'
                          label='Which one?'
                          width='100%'
                          options={serviceOptions}
                          mandatory
                        />
                      </Grid>
                    )}
                  </Grid>
                )}

                {/*
                  What the chosen service means, and — the part that decides the merchant's next
                  hour of work — whether ProShip can lend this courier its coverage, zones and
                  TAT, or whether they will be uploading their own.
                */}
                {chosenService && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1.25 }}>
                    <InfoOutlinedIcon
                      sx={{ fontSize: 15, color: chosenService.canInheritDefaults ? SUCCESS : WARNING, mt: '2px' }}
                    />
                    <Typography sx={bodySx}>
                      <strong>{chosenService.name}</strong>
                      {chosenService.slaDescription ? ` · ${chosenService.slaDescription}` : ''}
                      {chosenService.description ? ` — ${chosenService.description}` : ''}
                      {chosenService.canInheritDefaults
                        ? ' Serviceability, zones and TAT can start on ProShip’s for this service — you only upload your rate card.'
                        : ' ProShip has no account on this service to lend you, so you will need to upload your own serviceability, zones and TAT.'}
                    </Typography>
                  </Box>
                )}
                {/*
                  Say WHY the field is locked. A greyed-out select with no explanation reads as a
                  page that has not finished loading.
                */}
                {carrierName && directionLocked && (
                  <Typography sx={{ ...bodySx, mt: 1.25 }}>
                    ProShip integrates {carrierName} for{' '}
                    {directionOptions[0].label.toLowerCase()} shipments only, so the direction is
                    set for you. Hold a {directionOptions[0].value === 'RVP' ? 'forward' : 'reverse'}{' '}
                    contract with them? Tell your account manager — it needs an integration on our
                    side before you can connect it.
                  </Typography>
                )}
                <Typography sx={{ ...bodySx, mt: 1.25 }}>
                  The account code is permanent — it is what your carrier&rsquo;s webhook points at
                  and what every shipment on this courier code carries. Pick something you will
                  recognise in a rules screen a year from now.
                </Typography>
              </Step>

              <Step
                n={2}
                title={carrierName ? `${carrierName} credentials` : 'Credentials'}
                hint={
                  credFieldsApi.length > 0
                    ? `These are the fields ${carrierName} issues. They are on the API access email your account manager sent you.`
                    : 'The key and secret your carrier issued for API access.'
                }
              >
                {carrierName && !direction ? (
                  <Typography sx={bodySx}>Choose a direction above and this courier&rsquo;s own fields appear here.</Typography>
                ) : carrierName ? (
                  <>
                    {/*
                      Fields come from the server for this courier + direction. Only the ones the
                      merchant actually holds are listed — base URLs and protocol constants are
                      SYSTEM_DEFAULT and merged in server-side, which is why XpressBees asks for
                      5 things here rather than 15.
                    */}
                    {credFieldsApi.length === 0 ? (
                      <Typography sx={bodySx}>Loading {carrierName} fields…</Typography>
                    ) : (
                      <Grid container spacing={2}>
                        {credFieldsApi.map((f) => (
                          <Grid item xs={12} md={6} key={f.fieldKey}>
                            <RHFTextField
                              name={`cred.${f.fieldKey}`}
                              label={f.label}
                              mandatory={f.required}
                              type={f.fieldType === 'SECRET' ? 'password' : 'text'}
                              helperText={f.helpText || ''}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.md, mt: 1.5, flexWrap: 'wrap' }}>
                      <Typography sx={bodySx}>
                        ProShip uses these only to talk to {carrierName} on your behalf. They are
                        stored encrypted and never shown back once saved.
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography sx={bodySx}>Pick a carrier above and its own fields appear here.</Typography>
                )}
              </Step>

              <Step
                n={3}
                title='Courier limits'
                hint='What this courier will and will not carry. Allocation reads these before it hands a shipment over — a parcel above the ceiling goes to another courier instead of being rejected at pickup.'
              >
                <CourierMetaFields />
              </Step>

              <Step
                n={4}
                title='Connection test'
                hint='Run before saving. An account that cannot authenticate cannot ship, and finding that out here is cheaper than finding it out on a live order.'
              >
                {/*
                  One box, three claims — see TEST_SCOPE. It states what a pass proves before the
                  test runs, so a green tick is not read as "everything about this account works".
                */}
                <Box
                  sx={{
                    p: 1.5,
                    border: `1px solid ${testResult?.success ? SUCCESS : BORDER}`,
                    bgcolor: testResult?.success ? `${SUCCESS}0a` : 'transparent',
                    borderRadius: 1.5,
                  }}
                >
                  <Typography sx={{ ...strongSx, mb: 0.75 }}>
                    {testResult?.success ? 'Connection verified' : 'What this test will confirm'}
                  </Typography>
                  {(testResult?.success && testResult.verified?.length
                    ? testResult.verified
                    : TEST_SCOPE
                  ).map((claim) => (
                    <Box key={claim} sx={{ display: 'flex', gap: GAP.sm, alignItems: 'flex-start', mb: 0.4 }}>
                      {testResult?.success ? (
                        <CheckCircleIcon sx={{ fontSize: 15, color: SUCCESS, mt: '2px' }} />
                      ) : (
                        <RadioButtonUncheckedIcon sx={{ fontSize: 15, color: SUBTLE, mt: '2px' }} />
                      )}
                      <Typography sx={bodySx}>{claim}</Typography>
                    </Box>
                  ))}

                  {/*
                    What the probe could NOT reach. Carried next to the ticks rather than buried,
                    because a partial pass that reads as a full one is the failure this whole
                    screen exists to prevent.
                  */}
                  {testResult?.success && testResult.caveat && (
                    <Box sx={{ display: 'flex', gap: GAP.sm, alignItems: 'flex-start', mt: 0.75 }}>
                      <InfoOutlinedIcon sx={{ fontSize: 15, color: WARNING, mt: '2px' }} />
                      <Typography sx={{ ...bodySx, color: NAVY }}>{testResult.caveat}</Typography>
                    </Box>
                  )}
                  {/*
                    The carrier's own words, pass or fail. On a failure this is the whole value of
                    the screen — "Invalid licence key" is actionable, "test failed" is not.
                  */}
                  {testResult?.message && (
                    <Typography sx={{ ...bodySx, mt: 0.75, fontStyle: 'italic' }}>
                      {testResult.message}
                    </Typography>
                  )}
                </Box>

                {testResult && testResult.success === false && (
                  <Alert severity='error' sx={{ mt: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                    The carrier rejected this connection, so the account was not saved. Check the
                    credentials above and run the test again.
                    {/*
                      THE HOST WE ACTUALLY CALLED. Base URLs are SYSTEM_DEFAULT — merged
                      server-side and never shown on this form — so when a credential that works
                      elsewhere is rejected here, the host is the one cause the merchant cannot
                      see or deduce. Printing it turns "it works in Postman" into a one-glance
                      comparison.
                    */}
                    {testResult.endpoint && (
                      <Box component='div' sx={{ mt: 0.75, fontFamily: MONO, fontSize: SIZE['3xs'] }}>
                        Called: {testResult.endpoint}
                      </Box>
                    )}
                  </Alert>
                )}

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1.5 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: SUBTLE, mt: '2px' }} />
                  <Typography sx={bodySx}>
                    Labels are not tested here. ProShip prints the shipping label from your Label
                    Configuration, not from the carrier — the exception is Amazon Transportation
                    Services, which supplies its own.
                  </Typography>
                </Box>

                {testError && (
                  <Alert severity='warning' sx={{ mt: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                    {testError}
                  </Alert>
                )}

                {/*
                  Registration talks to PCIS and ProShip and can genuinely fail — no active plan,
                  the plan's courier limit reached, or the courier rejecting the credentials. The
                  server writes a sentence meant for the merchant; show it verbatim rather than a
                  generic failure, and stay on the page so the form is not lost.
                */}
                {saveError && (
                  <Alert severity='error' sx={{ mt: 1.5, fontFamily: FONT, fontSize: SIZE['2xs'] }}>
                    {saveError}
                  </Alert>
                )}

                <Divider sx={{ my: 2, borderColor: BORDER }} />

                <Box sx={{ display: 'flex', gap: GAP.md, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button
                    size='small'
                    onClick={() => runTest({
                      carrierName, accountCode, direction, mode, cred, cpId: watch('cpId'),
                    })}
                    disabled={!credsDone || testing}
                    startIcon={testing ? <CircularProgress size={14} /> : <ScienceOutlinedIcon />}
                    sx={secondaryBtnSx}
                  >
                    {testing ? 'Testing…' : 'Run connection test'}
                  </Button>
                  <Tooltip title={atLimit ? 'Your plan\u2019s active couriers are all in use. Switch one off, retire it, or upgrade.' : ''}>
                    <span>
                      <Button
                        type='submit'
                        variant='contained'
                        size='small'
                        disabled={!testDone || atLimit || saving}
                        sx={primaryBtnSx}
                      >
                        {saving ? 'Registering…' : 'Save account'}
                      </Button>
                    </span>
                  </Tooltip>
                  {!credsDone && (
                    <Typography sx={bodySx}>
                      {codeTaken
                        ? 'Choose an account code that is not already in use.'
                        : !accountDone
                        ? 'Fill in step 1 first.'
                        : `${missingCreds.length} credential field${missingCreds.length === 1 ? '' : 's'} still to fill.`}
                    </Typography>
                  )}
                </Box>
              </Step>
            </Box>
          </Grid>

          {/* ── Standing summary ── */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: { md: 'sticky' }, top: 12, display: 'flex', flexDirection: 'column', gap: GAP.lg }}>
              <Box sx={sectionCardSx}>
                <Typography sx={{ ...strongSx, mb: 0.75 }}>Where you are</Typography>
                <StepState done={accountDone}>Carrier, account code and service</StepState>
                <StepState done={credsDone}>
                  {carrierName ? `${carrierName} credentials` : 'Carrier credentials'}
                </StepState>
                <StepState done={metaDone}>Courier limits</StepState>
                <StepState done={testDone}>Connection tested</StepState>

                <Divider sx={{ my: 1.25, borderColor: BORDER }} />
                <Typography sx={bodySx}>
                  {allowed
                    ? `${counted} of ${allowed} active couriers used on the ${usage.plan?.name} plan.`
                    : `${counted} couriers connected.`}
                </Typography>
              </Box>

              <Box sx={sectionCardSx}>
                <Typography sx={{ ...strongSx, mb: 0.5 }}>Webhook URL</Typography>
                <Typography sx={{ ...bodySx, mb: 1 }}>
                  Configure this at {carrierName || 'the carrier'}&rsquo;s end so status updates
                  reach ProShip as they happen.
                </Typography>
                <WebhookPanel carrierName={carrierName} dense />
                <Typography sx={{ ...bodySx, mt: 0.75 }}>
                  {carrierName
                    ? `The same URL covers every ${carrierName} courier code you hold — set it once. ProShip matches each update to the right account by AWB.`
                    : 'Pick a carrier above and this becomes your real URL.'}
                </Typography>
              </Box>

              <Box sx={{ ...sectionCardSx, bgcolor: LIGHT_BG }}>
                <Typography sx={{ ...strongSx, mb: 0.5 }}>After you save</Typography>
                <Typography sx={bodySx}>
                  The account goes live for allocation straight away. Serviceability, zones, TAT and
                  status mapping fall back to ProShip&rsquo;s for this carrier until you upload your
                  own — the rate card is the one you have to supply, because ProShip does not hold
                  your rates and cannot compare a cost it cannot see.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </FormProvider>
    </Box>
  );
}

export default withLayout(ConnectCourier);
